import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Copy, Lock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LessonDocumentView from '../LessonDocumentView';
import { createShareLink, fetchSharePreview, fetchShareLinks, revokeShareLink } from './shareApi';

const STATUS_LABELS = { active: 'Actief', revoked: 'Ingetrokken', expired: 'Verlopen' };

const formatDateTime = (value) => (value
  ? new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
  : null);

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

// Beheer van publieke deellinks voor één gepubliceerde les. Een link wordt nooit automatisch
// aangemaakt; het token is alleen direct na het aanmaken zichtbaar (alleen de hash wordt bewaard).
const LessonShareDialog = ({ lesson, open, onOpenChange }) => {
  const [links, setLinks] = useState([]);
  const [expiresOn, setExpiresOn] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [created, setCreated] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!lesson) return;
    try {
      setLinks(await fetchShareLinks(lesson.id));
      setError(null);
    } catch (loadError) {
      setError(errorText(loadError, 'Kon de deellinks niet laden'));
    }
  }, [lesson]);

  const loadPreview = useCallback(async () => {
    if (!lesson) return;
    try {
      setPreview(await fetchSharePreview(lesson.id));
    } catch (previewError) {
      setPreview(null);
      setError(errorText(previewError, 'Kon de voorbeeldweergave niet laden'));
    }
  }, [lesson]);

  useEffect(() => {
    if (open) {
      setCreated(null); setExpiresOn(''); setAccessCode(''); setConfirmed(false); setPreview(null);
      load();
      loadPreview();
    }
  }, [open, load, loadPreview]);

  const shareUrl = created ? `${window.location.origin}${created.path}` : '';

  const create = async (event) => {
    event.preventDefault();
    if (busy || !confirmed || !preview) return;
    setBusy(true);
    try {
      const link = await createShareLink({
        lesson_id: lesson.id,
        expires_on: expiresOn || null,
        access_code: accessCode || null,
        confirm_public: true,
      });
      setCreated(link);
      setAccessCode('');
      setConfirmed(false);
      setError(null);
      await load();
    } catch (createError) {
      setError(errorText(createError, 'Link aanmaken mislukt'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (link) => {
    if (!window.confirm('Deze link intrekken? Hij werkt direct niet meer.')) return;
    try {
      await revokeShareLink(link.id);
      toast.success('Link ingetrokken');
      await load();
    } catch (revokeError) {
      toast.error(errorText(revokeError, 'Intrekken mislukt'));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link gekopieerd');
    } catch (copyError) {
      toast.error('Kopiëren mislukt');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dialog-sheet-mobile sm:max-h-[90vh] sm:max-w-lg sm:overflow-y-auto">
        <DialogHeader className="pr-12"><DialogTitle className="break-words max-sm:text-left max-sm:leading-snug">Publieke link — {lesson?.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">
          Iedereen met de link kan deze gepubliceerde les lezen en afdrukken, zonder Zoea-account. Trek de link in
          of depubliceer de les om de toegang direct te stoppen.
        </p>

        {error && <p role="alert" className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">{error}</p>}

        {created && (
          <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="created-link">
            <p className="text-sm font-medium text-emerald-900">Link aangemaakt. Kopieer hem nu: hij wordt niet opnieuw getoond.</p>
            <Input readOnly aria-label="Publieke link" value={shareUrl} onFocus={(event) => event.target.select()} />
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <Button type="button" size="sm" variant="outline" className="max-sm:h-11 max-sm:w-full" onClick={copy}><Copy className="mr-1 h-4 w-4" />Kopieer link</Button>
              <a className="max-sm:block" href={`https://wa.me/?text=${encodeURIComponent(`${lesson?.title || 'Connectles'}: ${shareUrl}`)}`} target="_blank" rel="noreferrer">
                <Button type="button" size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700 max-sm:h-11 max-sm:w-full"><MessageCircle className="mr-1 h-4 w-4" />WhatsApp</Button>
              </a>
            </div>
            {created.has_access_code && <p className="text-xs text-emerald-900">Deel de toegangscode via een ander kanaal dan de link.</p>}
          </div>
        )}

        <form onSubmit={create} className="space-y-3 rounded-md border border-dashed border-slate-300 p-3">
          <p className="text-sm font-medium text-slate-800">Nieuwe link</p>
          <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="share-warning">
            <p className="flex items-center gap-1 font-medium"><AlertTriangle className="h-4 w-4" />Controleer eerst wat openbaar wordt</p>
            <p>
              Iedereen met de link kan deze les lezen, ook buiten Zoea en zonder inloggen, en de link kan worden doorgestuurd.
              Hieronder zie je precies wat bezoekers krijgen. <strong>Niet opgenomen:</strong> gebedspunten (zowel van connectgroepen
              als uit de les), groepen, deelnemers, ontvangers en notities. Controleer de tekst zelf op namen, persoonlijke
              gegevens en vertrouwelijke informatie.
            </p>
          </div>
          <p className="text-xs text-slate-600" data-testid="share-prayer-note">
            Onderdeel 6 (Gebedsnoden) blijft in de publieke versie bewust leeg: interne gebedspunten worden niet openbaar gedeeld.
          </p>
          <div className="dialog-preview-box break-words rounded-md border border-slate-200 bg-white p-3" data-testid="share-preview" aria-label="Voorbeeld van de publieke les">
            {preview ? <LessonDocumentView lesson={preview.lesson} organization={preview.organization} showHeader /> : <p className="text-sm text-slate-500">Voorbeeld laden…</p>}
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <Checkbox checked={confirmed} onCheckedChange={(checked) => setConfirmed(!!checked)} disabled={!preview} aria-label="Ik heb het voorbeeld gecontroleerd" />
            <span>Ik heb het voorbeeld gecontroleerd en er staat niets in dat niet openbaar mag zijn.</span>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="share-expires" className="text-xs">Vervaldatum (optioneel)</Label>
              <Input id="share-expires" type="date" value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="share-code" className="text-xs">Toegangscode (optioneel)</Label>
              <Input id="share-code" type="text" autoComplete="off" minLength={8} maxLength={64} placeholder="min. 8 tekens" value={accessCode} onChange={(event) => setAccessCode(event.target.value)} />
            </div>
          </div>
          <div className="dialog-sheet-actions">
            <Button type="submit" size="sm" disabled={busy || !confirmed || !preview} className="bg-fuchsia-600 text-white hover:bg-fuchsia-700 max-sm:h-11 max-sm:w-full">Link aanmaken</Button>
          </div>
        </form>

        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-800">Bestaande links</p>
          {links.map((link) => (
            <div key={link.id} data-testid="share-link-row" className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-slate-200 p-3 text-sm">
              <div className="min-w-0 space-y-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={link.status === 'active' ? 'default' : 'secondary'}>{STATUS_LABELS[link.status]}</Badge>
                  {link.has_access_code && <span className="flex items-center gap-1 text-xs text-slate-500"><Lock className="h-3 w-3" />Met code</span>}
                </div>
                <p className="text-xs text-slate-500">Aangemaakt {formatDateTime(link.created_at)}</p>
                {link.expires_at && <p className="text-xs text-slate-500">Verloopt {formatDateTime(link.expires_at)}</p>}
                {link.revoked_at && <p className="text-xs text-slate-500">Ingetrokken {formatDateTime(link.revoked_at)}</p>}
                <p className="text-xs text-slate-500">{link.access_count} keer bekeken</p>
              </div>
              {link.status === 'active' && <Button type="button" size="sm" variant="outline" className="max-sm:h-11" onClick={() => revoke(link)}>Intrekken</Button>}
            </div>
          ))}
          {!links.length && <p className="text-sm text-slate-500">Nog geen links gemaakt.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LessonShareDialog;
